import { computed, defineComponent, ref, watch, useModel } from "vue";
import Svg from "../../core/svg";
import { addScopedGlobalListener, VueProp } from "../../core/vueHelpers";
import QuickEELSelector, { QuickEELOption } from "../../quickEELSelector/quickEELSelector";

const MonthSelectorClasses =
{
    monthSelector: "datePickerMonthSelector",
    button: "datePickerMonthSelector__button",
    selector: "datePickerMonthSelector__selector",
    selectorButton: "datePickerMonthSelector__selectorButton",
};

export default /*#__PURE__*/ defineComponent(
    {
        name: "monthSelector",
        emits:
        {
            "update:month": (_: number) => true,
            "update:year": (_: number) => true,
        },
        props:
        {
            yearHigh: VueProp(Number),
            yearLow: VueProp(Number),
            month: VueProp(Number),
            year: VueProp(Number),
        },
        setup(props)
        {
            const role = "combobox";
            const month = useModel(props, "month");
            const year = useModel(props, "year");
            const monthOptions: QuickEELOption[] =
                [
                    { value: 1, text: "January" },
                    { value: 2, text: "February" },
                    { value: 3, text: "March" },
                    { value: 4, text: "April" },
                    { value: 5, text: "May" },
                    { value: 6, text: "June" },
                    { value: 7, text: "July" },
                    { value: 8, text: "August" },
                    { value: 9, text: "September" },
                    { value: 10, text: "October" },
                    { value: 11, text: "November" },
                    { value: 12, text: "December" },
                ];
            const today = new Date();
            const currentYear = today.getFullYear();

            const yearOptions = computed(() =>
            {
                const options: QuickEELOption[] = [];

                for (let i = (props.yearHigh ?? currentYear + 10); i >= (props.yearLow); i--)
                {
                    const option: QuickEELOption =
                    {
                        value: i,
                        text: i.toString(),
                    };
                    options.push(option);
                }

                return options;
            });

            const disableIncrease = computed(() => month.value === 12 && year.value >= props.yearHigh);
            const disableDecrease = computed(() => month.value === 1 && year.value <= props.yearLow);

            const monthText = ref<string>(monthOptions.find(option => option.value === month.value)?.text ?? "");
            watch(month, (newValue) =>
            {
                monthText.value = monthOptions.find(option => option.value === newValue)?.text ?? "";
            });

            const yearText = ref<string>(yearOptions.value.find(option => option.value === year.value)?.text ?? "");
            watch(year, (newValue) =>
            {
                yearText.value = yearOptions.value.find(option => option.value === newValue)?.text ?? "";
            });

            watch(yearOptions, (newOptions) =>
            {
                yearText.value = newOptions.find(option => option.value === year.value)?.text ?? "";
            });

            const combinedAria = computed(() => `${getMonthLabel(0)} ${yearText.value}`);

            addScopedGlobalListener(document.body, "keydown", handleKeydown);

            return () => (
                <div class={MonthSelectorClasses.monthSelector}>
                    <span class="sr-only" aria-atomic={true} aria-live="polite">{combinedAria.value}</span>
                    <div role="button"
                        class={MonthSelectorClasses.button}
                        title="Previous Month"
                        aria-label={`Go to ${getMonthLabel(-1)}`}
                        aria-disabled={disableDecrease.value}
                        onClick={() =>
                        {
                            const { month: newMonth, year: newYear } = checkAndAdjustMonth(month.value, year.value, -1, props.yearHigh, props.yearLow);
                            month.value = newMonth;
                            year.value = newYear;
                        }}
                        tabindex={disableDecrease.value ? -1 : 0}
                    >
                        <Svg
                            name="ArrowLeft"
                            ignoreTabIndex={true}
                            showHoverEffect={false}
                            svgClasses={{
                                "ui-disabled": disableDecrease.value
                            }}
                            isButton={true} />
                    </div>
                    <QuickEELSelector 
                            class={MonthSelectorClasses.selector}
                            additionalButtonClasses={MonthSelectorClasses.selectorButton}
                            text={monthText}
                            title="Month"
                            ariaLabel="Search Month"
                            options={monthOptions}
                            v-model:value={month.value}
                            popupMaxHeight="16rem"
                            role={role}
                        />
                    <QuickEELSelector
                            class={MonthSelectorClasses.selector}
                            additionalButtonClasses={MonthSelectorClasses.selectorButton}
                            text={yearText}
                            title="Year"
                            ariaLabel="Search Year"
                            options={yearOptions.value}
                            v-model:value={year.value}
                            popupMaxHeight="16rem"
                            role={role}
                    />
                    <div role="button"
                        class={MonthSelectorClasses.button}
                        title="Next Month"
                        aria-label={`Go to ${getMonthLabel(1)}`}
                        aria-disabled={disableIncrease.value}
                        onClick={() =>
                        {
                            const { month: newMonth, year: newYear } = checkAndAdjustMonth(month.value, year.value, 1, props.yearHigh, props.yearLow);
                            month.value = newMonth;
                            year.value = newYear;
                        }}
                        tabindex={disableIncrease.value ? -1 : 0}
                    >
                        <Svg
                            name="ArrowRight"
                            ignoreTabIndex={true}
                            showHoverEffect={false}
                            svgClasses={{
                                "ui-disabled": disableIncrease.value
                            }}
                            isButton={true} />
                    </div>
                </div>
            );

            function getMonthLabel(delta: number)
            {
                let newMonth = ((month.value - 1 + delta) % 12) + 1;
                if (newMonth === 0)
                {
                    newMonth = 12;
                }
                return monthOptions.find(option => option.value === newMonth)?.text;
            }

            function handleKeydown(event: JQuery.KeyDownEvent)
            {
                const eventKeys = window.skyward.keys.getKeyNamesFromEvent(event);
                if (eventKeys.PAGE_UP)
                {
                    event.preventDefault();
                    if (eventKeys.SHIFT)
                    {
                        const newYear = year.value + 1;
                        if (props.yearHigh > newYear)
                        {
                            year.value = newYear;
                        }
                        return;
                    }

                    const { month: newMonth, year: newYear } = checkAndAdjustMonth(month.value, year.value, 1, props.yearHigh, props.yearLow);
                    month.value = newMonth;
                    year.value = newYear;
                }
                else if (eventKeys.PAGE_DOWN)
                {
                    event.preventDefault();
                    if (eventKeys.SHIFT)
                    {
                        const newYear = year.value - 1;
                        if (props.yearLow < newYear)
                        {
                            year.value = newYear;
                        }
                        return;
                    }

                    const { month: newMonth, year: newYear } = checkAndAdjustMonth(month.value, year.value, -1, props.yearHigh, props.yearLow);
                    month.value = newMonth;
                    year.value = newYear;
                }

                if ((event.target as HTMLElement).classList && (event.target as HTMLElement).classList.contains(MonthSelectorClasses.selectorButton))
                {
                    if (eventKeys.ARROW_DOWN)
                    {
                        event.preventDefault();
                        if ((event.target as HTMLElement).title === "Month")
                        {
                            const { month: newMonth, year: newYear } = checkAndAdjustMonth(month.value, year.value, -1, props.yearHigh, props.yearLow);
                            month.value = newMonth;
                            year.value = newYear;
                        }
                        else
                        {
                            const newYear = year.value - 1;
                            if (props.yearLow < newYear)
                            {
                                year.value = newYear;
                            }
                        }
                        
                    }
                    else if (eventKeys.ARROW_UP)
                    {
                        event.preventDefault();
                        if ((event.target as HTMLElement).title === "Month")
                        {
                            const { month: newMonth, year: newYear } = checkAndAdjustMonth(month.value, year.value, 1, props.yearHigh, props.yearLow);
                            month.value = newMonth;
                            year.value = newYear;
                        }
                        else
                        {
                            const newYear = year.value + 1;
                            if (props.yearHigh > newYear)
                            {
                                year.value = newYear;
                            }
                        }
                    }
                }
            }
        }
    }
);

function adjustMonth(month: number, year: number, delta: number)
{
    const date = new Date(year, month - 1 + delta);
    return { month: date.getMonth() + 1, year: date.getFullYear() };
}

export function checkAndAdjustMonth(month: number, year: number, delta: number, yearHigh: number, yearLow: number)
{
    const { month: newMonth, year: newYear } = adjustMonth(month, year, delta);
    if (yearLow <= newYear && newYear <= yearHigh)
    {
        return { month: newMonth, year: newYear };
    }

    return { month, year };
}